import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, errorText } from '../governance_v2/constants';
import { VISUAL_LABELS, MODULE_LABELS } from './constants';

const EMPTY = { code: '', name: '', family: '', purpose: '', when_to_use: '', when_not_to_use: '', required_data: '',
  operational_method: '', output_structure: '', visual_type: 'generic', owner_module: '', complementary_iads: '',
  limitations: '', example: '', why_template: '' };
const LONG = [['operational_method', 'Método operativo (pasos)'], ['output_structure', 'Estructura de salida (JSON del visual)'],
  ['required_data', 'Datos que requiere'], ['limitations', 'Limitaciones'], ['example', 'Ejemplo'], ['why_template', 'Plantilla del "por qué"']];
const SHORT = [['purpose', 'Para qué sirve'], ['when_to_use', 'Cuándo activarlo'], ['when_not_to_use', 'Cuándo NO usarlo']];

const Label = ({ children }) => <span className="mb-1 block text-xs font-medium text-slate-500">{children}</span>;

const IADCardFormDialog = ({ card, onClose }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(card ? { ...EMPTY, ...card, owner_module: card.owner_module || '', complementary_iads: (card.complementary_iads || []).join(', ') } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, owner_module: f.owner_module || null, is_active: card?.is_active ?? true,
        complementary_iads: f.complementary_iads.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean) };
      return card ? axios.put(`${API}/iad/catalog/${card.code}`, body) : axios.post(`${API}/iad/catalog`, body);
    },
    onSuccess: () => { toast.success(card ? 'Ficha actualizada (nueva versión)' : 'Ficha creada'); qc.invalidateQueries({ queryKey: ['iad-catalog'] }); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="iad-card-dialog">
        <DialogHeader><DialogTitle>{card ? `Editar ${card.code} · ${card.name}` : 'Nueva ficha IAD'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <label><Label>Código</Label><Input value={f.code} onChange={set('code')} disabled={!!card} placeholder="IAD-046" data-testid="iad-form-code" /></label>
          <label><Label>Nombre</Label><Input value={f.name} onChange={set('name')} data-testid="iad-form-name" /></label>
          <label><Label>Familia</Label><Input value={f.family} onChange={set('family')} data-testid="iad-form-family" /></label>
          <label><Label>Tipo de visual</Label>
            <select value={f.visual_type} onChange={set('visual_type')} className="h-9 w-full rounded-md border border-slate-300 bg-transparent px-2 text-sm dark:border-slate-700" data-testid="iad-form-visual">
              {Object.entries(VISUAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label><Label>Módulo dueño</Label>
            <select value={f.owner_module} onChange={set('owner_module')} className="h-9 w-full rounded-md border border-slate-300 bg-transparent px-2 text-sm dark:border-slate-700" data-testid="iad-form-module">
              <option value="">Sin módulo (corporativo)</option>
              {Object.entries(MODULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label><Label>IADs complementarios</Label><Input value={f.complementary_iads} onChange={set('complementary_iads')} placeholder="IAD-023, IAD-025" data-testid="iad-form-complementary" /></label>
          {SHORT.map(([k, l]) => <label key={k} className="col-span-3"><Label>{l}</Label><Input value={f[k]} onChange={set(k)} data-testid={`iad-form-${k}`} /></label>)}
          {LONG.map(([k, l]) => <label key={k} className="col-span-3"><Label>{l}</Label><Textarea value={f[k]} onChange={set(k)} rows={k === 'operational_method' ? 5 : 2} data-testid={`iad-form-${k}`} /></label>)}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="iad-form-cancel">Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="iad-form-save">{save.isPending ? 'Guardando…' : 'Guardar ficha'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IADCardFormDialog;
